/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { RichTextComponent } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { INewsletterBlockProps } from './newsletter-block.props.autogenerated';
import { format } from '@msdyn365-commerce-modules/utilities';

export interface INewsletterBlockViewProps extends INewsletterBlockProps<{}> {
    paragraph?: React.ReactNode;
    privacyTextM?: React.ReactNode;
    privacyTextP?: React.ReactNode;
    privacyTextT?: React.ReactNode;
    source?: React.ReactNode;
    className?: string;
    backgroundImage?: {
        src: string;
    };
}

/**
 *
 * NewsletterBlock component
 */
class NewsletterBlock extends React.PureComponent<INewsletterBlockProps<{}>> {
    public render(): JSX.Element | null {
        const localeString = this.props?.context?.request?.locale ? this.props?.context?.request?.locale?.toLowerCase() : '';

        const { paragraph, privacyTextM, className, privacyPolicyURL, useCrossCountryLabels } = this.props.config;
        const {
            htmlGenericMarketing,
            htmlProfiling,
            htmlThirdParty,
            htmlGenericMarketingCrossCountry,
            htmlProfilingCrossCountry,
            htmlThirdPartyCrossCountry
        } = this.props.resources;

        // Determine which consent texts to use based on configuration
        const effectiveHtmlGenericMarketing = useCrossCountryLabels ? htmlGenericMarketingCrossCountry : htmlGenericMarketing;
        const effectiveHtmlProfiling = useCrossCountryLabels ? htmlProfilingCrossCountry : htmlProfiling;
        const effectiveHtmlThirdParty = useCrossCountryLabels ? htmlThirdPartyCrossCountry : htmlThirdParty;

        const formattedGenericMarketing = `<p>${format(effectiveHtmlGenericMarketing, privacyPolicyURL)}</p>`;
        const formattedProfiling = `<p>${format(effectiveHtmlProfiling, privacyPolicyURL)}</p>`;
        const formattedThirdParty = `<p>${format(effectiveHtmlThirdParty, privacyPolicyURL)}</p>`;

        const text = paragraph && <RichTextComponent text={paragraph} />;

        const textPrivacyTextM = privacyTextM && (
            <RichTextComponent text={localeString !== 'en-us' ? formattedGenericMarketing : privacyTextM} />
        );
        const textPrivacyTextP = effectiveHtmlProfiling && <RichTextComponent text={formattedProfiling} />;
        const textPrivacyTextT = effectiveHtmlThirdParty && <RichTextComponent text={formattedThirdParty} />;

        const newsletterBlockViewProps = {
            ...this.props,
            paragraph: text,
            privacyTextM: textPrivacyTextM,
            privacyTextP: textPrivacyTextP,
            privacyTextT: textPrivacyTextT,
            className: classnames('ms-newsletter-block', className)
        };
        return this.props.renderView(newsletterBlockViewProps) as React.ReactElement;
    }
}
export default NewsletterBlock;
